import { NbDialogService } from '@nebular/theme';
import { getDocumentNumber, mongoId, safeStringify } from '../../@core/common/global';
import { SidebarCommon } from '../../@core/common/sidebar';
import { ApiService } from '../../@core/service/api.service';
import { AuthGuardService } from '../../@core/service/auth-guard.service';
import { SharedDataService } from '../../@core/service/shared-data.service';
import { BaseProductModel } from '../../@models/erp/baseProduct';
import { CompilationModel } from '../../@models/erp/compilation';
import { FinishedProductModel } from '../../@models/erp/finishedProduct';
import { Changelog, ChangelogEntry, ChangelogEntryType } from './shared/changelog/changelog.component';
import { CommonDialogComponent, NrkDialogButton, NrkDialogType } from './shared/common-dialog/common-dialog.component';


export type AbstractItemType = BaseProductModel | FinishedProductModel | CompilationModel;

export abstract class AbstractItem<T extends AbstractItemType> extends SidebarCommon<T> {

  public locked: boolean = false;
  public readOnly: boolean = true;
  protected dirtyString = '';
  protected prevDoc: T;
  protected copy: T;
  // protected businesses: BusinessModel[];
  // protected businessOptions: { id: string, name: string }[] = [];

  abstract items: T[];

  protected changelog: string = '';

  constructor(
    protected dialogService: NbDialogService,
    public api: ApiService,
    public auth: AuthGuardService,
    public data: SharedDataService,
  ) {
    super();
  }

  abstract createNewVersion();
  abstract validate(item: T): Promise<boolean>;
  abstract loadItems(): Promise<void>;
  abstract saveItem(item: T, draft: boolean): Promise<T>;
  abstract isDirty(): boolean;

  getBusinessName(item: string) {
    const b = item && this.data.businesses.find(f => mongoId(f) === mongoId(item));
    return b && b.name ? b.name : '';
  }

  getItems(items: any[] = null): any[] {
    if (!items) items = this.items;
    return items?.sort((a, b) => (a.code < b.code ? 1 : -1));
  }

  static count = 0;

  getCurrentItems(items: any[] = null, withDrafts: boolean = false): any[] {
    if (!items) items = this.items;
    const ptable = [];
    const codes = [];
    if (items) items.sort((a, b) => a.version < b.version ? 1 : -1).forEach(el => {
      if (!codes.includes(el.code) && (!el.draft || withDrafts)) {
        codes.push(el.code);
        // el.calcName = el.calcName.replace(/ *\(VS\d+\)/, '');
        ptable.push(el);
      }
    });
    return ptable.sort((a, b) => a.code < b.code ? 1 : -1);
  }

  getVersions(item: T): T[] {
    return this.items && item &&
      this.items.filter(f => f.code === item.code).sort((a, b) => a.version < b.version ? -1 : 1);
  }

  isFirstVersion() {
    return this.selected && this.selected.version === 1 && this.getMaxVersion(this.selected) === 1;
  }

  edit() {
    this.locked = false;
    this.changelog = 'Edit';
    this.selected.editCount++;
    this.addChangelogEntry(this.selected);
    this.copy = JSON.parse(safeStringify(this.selected));
  }

  getMaxVersion(item: T): number {
    const vers = this.getVersions(item);
    if (!vers || !vers.length) return 0;
    return vers.map(m => m.version).reduce((a, b) => Math.max(a, b));
  }

  switchToVersion(item: T) {
    this.selected = item;
    this.dirtyString = safeStringify(this.selected);
    this.locked = !item.draft;
  }

  canUnlock() {
    const max = this.getMaxVersion(this.selected);
    return max < 2 || this.selected.version === max;
  }

  lock(doLock: boolean = true) {
    this.locked = doLock;
    if (!doLock) {
      this.createNewVersion();
      this.items.push(this.selected);
      this.addChangelogEntry(this.selected);
      this.copy = JSON.parse(safeStringify(this.selected));
    }
  }

  async cancel(item: T, force: boolean = false) {
    if (force || await this.canDismiss(item)) {
      this.loadItems()
      this.hideSidebar();
    }
  }

  async cancelDraft(item: T) {
    await this.loadItems();
    this.selected = this.getVersions(item).reverse()[0];
    if (!this.selected) {
      this.cancel(item, true);
    } else {
      this.switchToVersion(this.selected);
    }
  }

  async save(item: T, draft: boolean = false): Promise<T> {
    const valid = await this.validate(item);
    if (!draft && !valid) {
      return null;
    }
    item.draft = draft;
    if (!draft && !this.selected.draft) {
      this.lock();
    }
    // this.saveChangelog(this.selected);
    this.selected = await this.saveItem(item, draft);
    this.copy = JSON.parse(safeStringify(this.selected));
    return this.selected;
  }

  async reload(item: T): Promise<T> {
    // if (!draft && bp && bp._id) this.api.downloadBaseItemPDF(bp);
    await this.loadItems();
    this.selected = this.items.find(f => f.code === item.code && f.version === item.version);
    this.dirtyString = safeStringify(this.selected);
    return this.selected;
  }

  async toggleSidebar(item: T) {
    if (!item) return;
    if (!item.draft) {
      this.lock();
    } else {
      this.locked = false;
    }
    this.dirtyString = safeStringify(item);
    this.selected = item;
    this.loadChangelog(this.selected);
    super.toggleSidebar(item);
  }

  canDismiss(item: T): Promise<boolean> {
    return new Promise(resolve => {
      if (this.isDirty()) {
        this.dialogService.open(CommonDialogComponent, { context: { dialogType: NrkDialogType.dismiss } })
          .onClose.subscribe(async x => {
            if (x.button === NrkDialogButton.save) {
              if (!await this.save(item)) {
                resolve(false)
              }
            }
            if (x.button === NrkDialogButton.dismiss) {
              resolve(true);
            }
            resolve(false)
          });
      } else {
        resolve(true);
      }
    });
  }

  updateItem(item: T) {
    this.addItem(item);
  }

  addItem(item: T) {
    // console.log('add item', item);
    const el = this.items.find(f => f.code === item.code && f.version === item.version);
    if (el) {
      this.items[this.items.indexOf(el)] = item;
    } else {
      this.items.unshift(item);
    }
  }

  removeItem(item: T) {
    // console.log('remove item', item);
    const idx = this.items.indexOf(this.items.find(f => f._id === item._id));
    this.items.splice(idx, 1);
  }

  copyToClipboard(toCopy: string): void {
    // const createCopy = (e: ClipboardEvent) => {
    //   e.clipboardData.setData('text/plain', toCopy);
    //   e.preventDefault();
    // };
    // document.addEventListener('copy', createCopy );
    // document.execCommand('copy');
    // document.removeEventListener('copy', createCopy );
    this.data.copyToClipboard(toCopy);
  }

  getProductLink(){
    return location.href.split('?')[0] + '?code=' + this.selected.code
  }

  // getUserShort(userId: string) {
  //   const user = this.api.users && this.api.users.find(u => u._id === mongoId(userId));
  //   return user ? user.short : '';
  // }

  // getChangeType(changeType: number) {
  //   return ChangelogEntry.typeToString(changeType);
  // }

  async loadChangelog(item: T) {
    if (typeof item.changelog === 'string' || !item.changelog) {
      // TODO: convert all changelog strings to ChangelogEntry in database & remove legacy code
      const versions = this.items.filter(f => f.code === item.code).sort((a, b) => a.version < b.version ? 1 : -1);
      const items = [] as any;
      for (let idx = 0; idx < versions.length; idx++) {
        const v = versions[idx];
        if ((v as any).documentStatus) { // => compilationModel
          const doc = await this.api.loadCompilation(mongoId(v));
          doc['prevDocumentNumber'] = getDocumentNumber(idx < versions.length-1 && versions[idx+1] as any)
          items.push(doc);
        } else {
          items.push(v)
        }
      }
      // if ((item as any).product || (item as any).trial) { // finished product or base product
      //   const entries = items.map(v => ({
      //     version: v.version,
      //     date: v.updatedAt,
      //     user: v.owner || v.issuedBy,
      //     type: v.version === 1 ? ChangelogEntryType.init : ChangelogEntryType.newVersion,
      //     text: v.changelog,
      //     showInPdf: true,
      //   })) as ChangelogEntry[];
      //   this.changelog = item.changelog as string;
      //   item.changelog = entries;
      // } else {  // compilation
      const entries = items.map(v => ({
        version: v.version,
        date: v.updatedAt,
        user: v.owner || v.issuedBy,
        type: v.version === 1 ? ChangelogEntryType.init : ChangelogEntryType.newVersion,
        documentNumber: getDocumentNumber(v),
        change: v.version === 1 ? 'INI' : `${v['prevDocumentNumber']} to ${getDocumentNumber(v as any)}`,
        items: [{ quickText: '', text: v.changelog }],
        showInPdf: true,
      })) as ChangelogEntry[];
      item.changelog = entries;
      // }
    }
    if (!item.changelog || !item.changelog[0]) this.addChangelogEntry(item);
  }

  addChangelogEntry(item: T) {
    const log = item.changelog as ChangelogEntry[];
    let type = ChangelogEntryType.edit;
    let change = `Edit ${getDocumentNumber(item as any)}`
    if (!item.changelog || item.changelog.length === 0) {
      item.changelog = [];
      type = ChangelogEntryType.init;
      change = 'INI';
    }
    if (!(item.changelog as ChangelogEntry[]).find(i => i.version === item.version) && item.version > 1) {
      type = ChangelogEntryType.newVersion;
      change = `${getDocumentNumber(this.prevDoc as any)} to ${getDocumentNumber(item as any)}`;
    }

    if (Changelog.sameChangelogBaseEntry(log?.[0], null, this.auth.user, change, getDocumentNumber(item))) {
      if (log?.[0].items.length === 0) {
        log[0].items = [{ quickText: '', text: '' }];
        return;
      }
    }

    const entry = new ChangelogEntry();
    entry.version = item.version;
    entry.date = new Date();
    entry.user = mongoId(this.auth.user);
    entry.type = type;
    entry.documentNumber = getDocumentNumber(item as any);
    entry.change = change; // compilation only
    entry.items = [{ quickText: '', text: '' }]; // compilation only
    entry.showInPdf = true;

    (item.changelog as ChangelogEntry[]).unshift(entry);
  }

  async saveChangelog(item: T, silent = false, askForKeepOldIssueDate = false): Promise<void> {
    try {
      const entry = Object.assign({}, this.selected.changelog[0] as ChangelogEntry);
      entry.date = new Date();
      entry.user = mongoId(this.auth.user);
      if (!entry.previousDoc && this.prevDoc) {
        entry.previousDoc = { code: this.prevDoc.code, version: this.prevDoc.version, editCount: this.prevDoc.editCount };
      }
      const fromDraft = /DRAFT/.test(entry.documentNumber);
      const draftDocNumber = entry.documentNumber;
      const docNumber =  getDocumentNumber(item as any);
      const prevDocNumber = getDocumentNumber(entry.previousDoc as any);
      if (!this.selected.draft) {
        entry.documentNumber = docNumber;
        switch (entry.type) {
          case ChangelogEntryType.newVersion:
            entry.change = `${prevDocNumber} to ${docNumber}`;
            break;
          case ChangelogEntryType.init:
          case ChangelogEntryType.draft:
            entry.change = 'INI' + (prevDocNumber ? ` from ${prevDocNumber}` : '');
            break;
          default:
            entry.change = `Edit ${docNumber}`;
        }
      } else if (entry.type === ChangelogEntryType.draft || entry.type === ChangelogEntryType.init) {
        entry.change = 'INI' + (prevDocNumber ? ` from ${prevDocNumber}` : '');
      }
      entry.items = entry.items.filter(i => (i.quickText && i.quickText.length) || (i.text && i.text.length));
      const changes = await Changelog.changeMap(this.copy, this.selected);
      entry.items.unshift(...changes);
      const log = this.selected.changelog as ChangelogEntry[];
      if (fromDraft && !this.selected.draft && this.selected.version === 1 && entry.items.length) {
        log[0].items.unshift({ quickText: '', text: 'Document finalized' });
        if (log[0].type !== ChangelogEntryType.clone) log[0].type = ChangelogEntryType.draft;
        entry.type = ChangelogEntryType.init;
        entry.change = `${draftDocNumber} to ${docNumber}`;
        entry.items = [];
        (this.selected.changelog as ChangelogEntry[]).unshift(entry);
      } else {
        log[0] = entry;
      }

      // // TODO: Object.assign in api service
      // const changes = Changelog.changeMap(this.copy, this.selected);
      // // const autolog = new ChangelogEntry(this.selected, this.auth.user);
      // // autolog.items = changes;
      // // (this.selected.changelog as ChangelogEntry[]).splice(1, 0, autolog);
      // entry.items.unshift(...changes);
      // console.log('doc changes', changes);

      const finalize = (dialogResponse = null) => {
        if (dialogResponse) {
          entry.items = dialogResponse.changelog.items;
          entry.dirty = dialogResponse.changelog.dirty;
          if (changes.length > 0 && entry.type === ChangelogEntryType.edit && !entry.dirty) {
            entry.type = ChangelogEntryType.autolog;
          }
          if (entry.dirty && entry.type === ChangelogEntryType.autolog) {
            entry.type = ChangelogEntryType.edit;
          }
        }
        if (log.length > 1
          && new Date(log[0].date).toDateString() === new Date(log[1].date).toDateString()
          && log[0].change === log[1].change
          && log[0].documentNumber === log[1].documentNumber
          && log[0].type === ChangelogEntryType.autolog
          && (
            log[1].type === ChangelogEntryType.autolog
            || (log[1].items.length === 0 && log[1].type === ChangelogEntryType.edit)
          )
        ) {
          log[1].items.unshift(...log[0].items);
          log[1].date = log[0].date;
          log[1].type = ChangelogEntryType.autolog;
          log.shift();
        }
        log[0].items = log[0].items.filter(f =>
          (f.quickText && f.quickText.length > 0) || (f.text && f.text.length > 0)
        );
      }

      if (silent) return;

      return new Promise(resolve => {
        this.dialogService.open(CommonDialogComponent, {
          closeOnBackdropClick: true,
          context: {
            dialogType: NrkDialogType.changelog,
            changelog: Object.assign({}, entry),
            compilation: item as any,
            keepOldIssueDate: askForKeepOldIssueDate ? false : null,
          },
        }).onClose.subscribe(x => {
          finalize(x);
          resolve();
        })
      })
    } catch (e) {
      console.error(e);
    }
  }
}
